import _ from 'lodash';
import { ErrorReporter } from '@wix/editor-error-reporter';
import * as util from '#packages/util';
import * as stateManagement from '#packages/stateManagement';
import * as superAppMenuInfra from '#packages/superAppMenuInfra';
import * as tpaUtils from '../utils/tpaUtils';
import constants from '../constants/constants';
import * as bi from '../bi/bi';
import superAppsConstants from './superAppsConstants';
import * as tpaAddRemoveDSService from '../services/tpaAddRemoveDSService';
import * as packagePicker from '../packagePicker/packagePicker';
import * as tpaHelpService from '../services/tpaHelpService';
import * as tpaStyleService from '../services/tpaStyleService';
import * as tpaAlertsService from '../services/tpaAlertsService';
import * as dashboardAppService from '../services/dashboardAppService';
import * as tpaSettingsModalService from '../services/tpaSettingsModalService';
import * as tpaAppInstallingService from '../services/tpaAppInstallingService';
import { getAppType } from '../appMarket/utils/appMarketUtils';
import { platformBI, type PlatformOrigin } from '#packages/platform';
import {
  reportBiForAddedComponentToStage,
  type ReportBiForAddedComponentToStageOptions,
} from '../utils/tpaUtils';

import type { EditorAPI } from '#packages/editorAPI';
import type {
  PlatformAppDescription,
  CompLayout,
  CompRef,
  StyleRefOrStyleRefs,
  CompStructure,
  Callback1,
  AppMarketAppData,
  Rect,
} from 'types/documentServices';
import tpaConstants from '../../tpa/constants/constants';
import type { AppData } from '#packages/privateAppsPanel';
import experiment from 'experiment';
import { EditorPlatformHostIntegrationAPI } from '@wix/editor-platform-host-integration-apis';
import { isPlatformSdkEventHanlersEnabled } from '../experiments/experiments';

let editorAPI: EditorAPI;
let platformAPI: EditorPlatformHostIntegrationAPI;

const WIDGET_OPTIONS = [
  'dontStretch',
  'styleId',
  'parentContainerRef',
  'containerRef',
  'presetId',
  'platformOrigin',
  'category',
  'section',
  'addingMethod',
  'origin',
  'componentName',
  'componentDefinition',
  'useRelativeToContainerLayout',
];

const init = function (_editorAPI: EditorAPI) {
  editorAPI = _editorAPI;
  platformAPI = editorAPI.host.getAPI(EditorPlatformHostIntegrationAPI);
};

const isPremium = function (appDefinitionId: string) {
  return editorAPI.dsRead.tpa.app.isPremiumByAppDefinitionId(appDefinitionId);
};

const getExtensions = function (
  appDefinitionId: string,
  callback: Callback1<AnyFixMe>,
) {
  if (callback && appDefinitionId) {
    editorAPI.dsRead.tpa.appMarket.getDataAsync(appDefinitionId).then(function (
      data: AppMarketAppData,
    ) {
      callback(editorAPI.dsRead.tpa.app.getExtensionsWidgets(data));
    });
  }
};

function sendTpaAddedBiEvent(appData: AppMarketAppData) {
  const compData = editorAPI.selection.getSelectedComponentData();
  const compId = editorAPI.selection.getSelectedComponentId();
  const pageId = editorAPI.dsRead.pages.getFocusedPageId();
  const isPermissionsApp = appData.by !== 'Wix';
  tpaUtils.bi.reportTpaAddedBiEvent(
    editorAPI,
    appData.appDefinitionId,
    compData?.widgetId,
    pageId,
    compId,
    true,
    isPermissionsApp,
    {
      origin: 'template_provision',
      adding_method: 'template',
    },
  );
}

const openDashboardUrl = function (
  appDefinitionId: string,
  options?: AnyFixMe,
  biParams?: AnyFixMe,
) {
  options = options || {};

  const type = biParams?.type || constants.BI.type.GFPP_MANAGE;
  fireAppIntentBI(type, appDefinitionId);
  const appData = editorAPI.dsRead.tpa.app.getDataByAppDefId(
    appDefinitionId,
  ) as AppMarketAppData;
  const isDraftMode = editorAPI.dsRead.generalInfo.isDraft();
  if (appData) {
    // @ts-expect-error
    if (appData.notProvisioned || appData.demoMode || isDraftMode) {
      const onSuccess = function () {
        options.bi = tpaUtils.bi.getAppIntentBiParamsToAddToUrl(
          editorAPI,
          appDefinitionId,
          type,
        );
        sendTpaAddedBiEvent(appData);
        openDashboardUrlAfterProvision(appData, options);
      };
      const onError = _.partial(
        tpaAlertsService.openSaveFailedAlertForDashboardUrl,
        editorAPI,
        appData.appDefinitionId,
      );
      editorAPI.saveManager.saveInBackground(
        onSuccess,
        onError,
        'openDashboardUrl',
        {
          sourceOfStart: 'openDashboardUrl_bgSave',
        },
      );
    } else {
      openDashboardUrlAfterProvision(appData, options);
    }
  }
};

const openDashboardUrlAfterProvision = function (
  appData: AppMarketAppData,
  params: AnyFixMe,
) {
  const settingModalName = constants.TPA_SETTING_MODAL;
  const modal = tpaUtils.getOpenedPanel(
    editorAPI.panelManager,
    settingModalName,
  );
  if (modal) {
    editorAPI.panelManager.closePanelByName(modal.props.panelName);
  }

  const compId = util.guidUtils.getUniqueId(undefined, undefined);

  const modalProps: {
    title: string;
    options: AnyFixMe;
    compId: string;
    isBareMode?: boolean;
    width?: string;
    height?: string;
    url?: string;
    onClose?: AnyFixMe;
    shouldNotCloseOnBlur?: boolean;
    appDefinitionId?: string;
    helpId?: string;
    widgetId?: string;
  } = {
    title: params.title || '',
    isBareMode: params.isBareMode || false,
    options: params.options,
    compId,
  };

  const dashboardUrl = dashboardAppService.getDashboardUrl(
    editorAPI,
    appData as AppData,
    compId,
    params,
  );
  if (dashboardUrl) {
    if (isPlatformSdkEventHanlersEnabled) {
      platformAPI.applications.addAppToRefresh([appData.appDefinitionId]);
    } else {
      editorAPI.store.dispatch(
        stateManagement.tpa.actions.addAppDefIdsToRefreshOnDashboardClose([
          appData.appDefinitionId,
        ]),
      );
    }

    modalProps.isBareMode = false;
    modalProps.width = '100%';
    modalProps.height = '100%';
    modalProps.url = dashboardUrl;
    modalProps.onClose = _.partial(refreshVisitedApps, params.onClose);
    modalProps.shouldNotCloseOnBlur = params.shouldNotCloseOnBlur;
    const leavePanelsOpen = params.leavePanelsOpen || false;
    tpaHelpService
      .getHelpId(
        editorAPI,
        appData.appDefinitionId,
        params.widgetId,
        tpaHelpService.HELP_END_POINTS.SETTINGS_MODAL,
      )
      .then(function (helpId: unknown) {
        modalProps.appDefinitionId = appData.appDefinitionId;
        modalProps.helpId = helpId as string;
        modalProps.widgetId = params.widgetId;
        if (params.widgetless) {
          editorAPI.panelManager.openPanel(
            settingModalName,
            modalProps,
            leavePanelsOpen,
          );
        } else {
          tpaUtils.selectComponentAndNavigateIfNeeded(
            editorAPI,
            { ...appData, compId },
            function () {
              editorAPI.panelManager.openPanel(
                settingModalName,
                modalProps,
                leavePanelsOpen,
              );
            },
          );
        }
      });
  }
};

const openTranslationDashboardUrl = function (
  appDefinitionId: string,
  options: Record<string, AnyFixMe> = {},
  biParams: AnyFixMe,
) {
  options.isTranslationDashboard = true;
  return openDashboardUrl(appDefinitionId, options, biParams);
};

const refreshVisitedApps = function (onClose: () => void) {
  if (isPlatformSdkEventHanlersEnabled) {
    platformAPI.applications.refreshVisitedApps();
    return;
  }

  const editorState = editorAPI.store.getState();
  const appIdsToRefresh =
    stateManagement.tpa.selectors.getAppIdsToRefreshAfterDashboardClose(
      editorState,
    );
  const shouldRefreshGeneralInfo =
    stateManagement.tpa.selectors.getShouldRefreshGeneralInfo(editorState);
  refreshAppComps(appIdsToRefresh);
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/map
  const appDefinitionIds = _.map(
    appIdsToRefresh,
    (applicationId) =>
      editorAPI.dsRead.platform.getAppDataByApplicationId(applicationId)
        ?.appDefinitionId,
  );
  refreshAppsLivePreview(appDefinitionIds);
  editorAPI.store.dispatch(
    stateManagement.tpa.actions.setAppDefIdsToRefreshOnDashboardClose([]),
  );
  if (shouldRefreshGeneralInfo) {
    editorAPI.documentServices.generalInfo.sitePropertiesInfo.reload();
    editorAPI.store.dispatch(
      stateManagement.tpa.actions.shouldRefreshGeneralInfo(false),
    );
  }
  if (onClose) {
    onClose();
  }
};

const refreshAppCompsForAppDefId = function (appDefinitionId: string) {
  const appData = editorAPI.dsRead.tpa.app.getDataByAppDefId(appDefinitionId);
  refreshAppComps([appData.applicationId]);
  refreshAppsLivePreview([appDefinitionId]);
};

const refreshAppComps = function (applicationIds: number[] | string[]) {
  const appComps = experiment.isOpen('se_appDefIdUpdates')
    ? // @ts-expect-error
      editorAPI.dsRead.tpaV2.app.getAllComps(applicationIds)
    : editorAPI.dsRead.tpa.app.getRenderedReactCompsByApplicationId(
        applicationIds as number[],
      );

  editorAPI.dsActions.tpa.app.refreshApp(appComps);
};

const refreshAppsLivePreview = function (appDefinitionIds: string[]) {
  editorAPI.dsActions.platform.livePreview.refresh({
    apps: appDefinitionIds,
    source: 'editorRefreshAppComps',
  } as AnyFixMe);
};

const registerOnAppInstalled = function (
  appDefinitionId: string,
  callback: Callback1<any>,
) {
  editorAPI.dsRead.tpa.app.registerOnInstalled(appDefinitionId, callback);
};

const registerOnAppDeleted = function (
  appDefinitionId: string,
  callback: Callback1<any>,
) {
  editorAPI.dsRead.tpa.app.registerOnDeleted(appDefinitionId, callback);
};

//multi multi API
const addSection = async function (
  appDefinitionId: string,
  widgetId: number,
  origin?: string,
) {
  const appData = editorAPI.dsRead.tpa.app.getDataByAppDefId(appDefinitionId);
  const widgetData = appData.widgets[widgetId];
  if (
    widgetData &&
    tpaUtils.isMultiSectionInstanceEnabled(appData, widgetData.widgetId)
  ) {
    return new Promise<void>((resolve) => {
      const callback = function (obj: AnyFixMe) {
        editorAPI.pages.navigateTo(obj.page.id, () => {
          resolve();
        });
        // @ts-expect-error
        sendSectionBiEvent(appData, obj, widgetData.widgetId, origin);
      };

      tpaUtils.bi.reportBIOnAppIntent(
        editorAPI,
        bi.events.APP_INTENT,
        appDefinitionId,
        constants.BI.initiator.EDITOR,
        constants.BI.type.ADD_WIDGET,
      );
      tpaAddRemoveDSService.addMultiSection(
        editorAPI,
        appData.appDefinitionId,
        {
          // @ts-expect-error
          pageId: widgetData.appPage.id,
          callback,
        },
      );
    });
  }
};

const addApp = function (
  appDefId: string,
  options: {
    callback?: Function;
    biOrigin?: string;
    showProgressBar?: boolean;
    platformOrigin?: PlatformOrigin;
    isPreInstallation?: boolean;
    origin?: PlatformOrigin;
  } = {
    showProgressBar: true,
    isPreInstallation: false,
  },
) {
  if (!appDefId) return;
  const errorForFailure = new Error('Error while installing -');
  const origin = options.biOrigin || constants.BI.type.ADD_APP;
  let appName: string;
  if (!options.isPreInstallation) {
    tpaUtils.bi.reportBIOnAppIntent(
      editorAPI,
      bi.events.APP_INTENT,
      appDefId,
      constants.BI.initiator.EDITOR,
      origin,
    );
  }

  if (options.platformOrigin) {
    options.origin = options.platformOrigin;
    delete options.platformOrigin;
  }

  return editorAPI.dsRead.tpa.appMarket
    .getDataAsync(appDefId)
    .then(function (marketData: AppMarketAppData) {
      appName = marketData?.name;
      errorForFailure.message += appName;
      tpaAppInstallingService.prePageAppInstalling(
        editorAPI,
        appName,
        appDefId,
        {
          origin,
          showProgressBar: options.showProgressBar,
          showPageAddedPanel: true,
        },
      );
      platformBI.reportBIOnPlatformTryToInstall(editorAPI, {
        app_id: appDefId,
        app_name: appName,
        origin: options?.origin?.info?.type as unknown as PlatformOrigin,
      });

      const callback = options.callback || _.noop;
      options.callback = function (data: AnyFixMe) {
        editorAPI.tpa.notifyAppInstalled(appDefId, {
          platformOrigin: options.origin,
        });
        platformBI.reportBIOnPlatformAppAdded(editorAPI, {
          app_id: appDefId,
          app_name: appName,
          origin: options?.origin?.info?.type as unknown as PlatformOrigin,
        });
        tpaAppInstallingService.onPageAppInstallingCompleted(
          editorAPI,
          appName,
          appDefId,
          data,
          {
            origin,
          },
        );
        callback(data);
      };
      const type = getAppType(marketData as AppData);
      switch (type) {
        case tpaConstants.APP.TYPE.PLATFORM_ONLY:
          tpaAddRemoveDSService.addPlatform(editorAPI, appDefId, options);
          break;
        case tpaConstants.APP.TYPE.SECTION:
          tpaAddRemoveDSService.addSection(editorAPI, appDefId, options);
          break;
        case tpaConstants.APP.TYPE.WIDGET:
          tpaAddRemoveDSService.addWidget(
            editorAPI,
            appDefId,
            options as AnyFixMe,
          );
          break;
      }
    })
    .catch(function (e = {}) {
      if (!e.stack) {
        errorForFailure.message += `${e.message} ${errorForFailure.message}`;
        ErrorReporter.captureException(errorForFailure, {
          tags: { tpaInstallation: appName, origin },
        });
      } else {
        ErrorReporter.captureException(e, {
          tags: { tpaInstallation: appName, origin },
        });
      }
      options.callback(e);
    });
};

const onRefWidgetClick = function (
  appDefinitionId: string,
  panelName: string,
  event: { data: any; layout: any },
) {
  addRefWidget(appDefinitionId, panelName, event.data, event.layout);
};

const onRefWidgetDrop = (
  appDefinitionId: string,
  panelName: string,
  position: { width: string; height: string },
  event: {
    structure: { layout: { width: string; height: string }; data: AnyFixMe };
  },
) => {
  position.width = event.structure.layout.width;
  position.height = event.structure.layout.height;
  addRefWidget(appDefinitionId, panelName, event.structure.data, position);
};

const addRefWidget = function (
  appDefinitionId: string,
  panelName: string,
  widgetData: { widgetId: number; variationId: string },
  layout: any,
) {
  const page = editorAPI.pages.getFocusedPage();
  const addPosition = editorAPI.pasteLogic.addPanelPasteLogic.getPastePosition(
    editorAPI,
    layout,
    page.id,
  );

  const componentDef = {
    componentType: 'wysiwyg.viewer.components.RefComponent',
    layout: _.defaults(addPosition, layout),
    style: 'ref1',
    data: {
      type: 'WidgetRef',
      appDefinitionId,
      widgetId: widgetData.widgetId,
      variationId: widgetData.variationId,
    },
  };

  editorAPI.components.add(
    page,
    componentDef,
    undefined,
    undefined,
    constants.BI.type.ADD_WIDGET,
  );
  editorAPI.panelManager.closePanelByName(panelName);
};

interface AddAppBuilderWidgetToSiteOptions {
  biOptions: Omit<
    ReportBiForAddedComponentToStageOptions,
    'appDefId' | 'origin'
  >;
  containerRef?: CompRef;
}

function addAppBuilderWidgetToSite(
  appDefinitionId: string,
  widgetData: {
    installationType: string;
    widgetId: string;
    variationId: any;
    scopedPresets: any;
  },
  layout: Rect,
  overriddenData: unknown,
  options: AddAppBuilderWidgetToSiteOptions = {} as AddAppBuilderWidgetToSiteOptions,
): Promise<CompRef> {
  const installationType = (widgetData.installationType || 'closed') as
    | 'open'
    | 'closed';
  const biOptions = options?.biOptions || {};

  return new Promise((resolve) => {
    editorAPI.dsActions.appStudioWidgets.addWidget(
      appDefinitionId,
      widgetData.widgetId,
      {
        // @ts-expect-error
        variationPageId: widgetData.variationId,
        layout,
        installationType,
        overriddenData,
        scopedPresets: widgetData.scopedPresets,
        containerRef: options.containerRef,
        onSuccess: (newWidgetRef) => {
          editorAPI.selection.selectComponentByCompRef(newWidgetRef);
          editorAPI.history.add('Add widget', { isAddingComponent: true });
          const appData =
            editorAPI.dsRead.tpa.app.getDataByAppDefId(appDefinitionId);
          // @ts-expect-error
          sendAddWidgetBiEvent(appData, newWidgetRef, widgetData.widgetId);

          if (newWidgetRef && editorAPI.components.is.exist(newWidgetRef)) {
            reportBiForAddedComponentToStage(editorAPI, newWidgetRef, {
              ...biOptions,
              appDefId: appData.appDefinitionId,
              origin: constants.BI.type.ADD_WIDGET,
            } as AnyFixMe);
          }

          resolve(newWidgetRef);
        },
      },
    );
  });
}
export type AddWidgetBIParams = tpaAddRemoveDSService.AddWidgetBIParams;

export type AddWidgetOptions = {
  presetId?: string;
  biOrigin?: string;
  platformOrigin: PlatformOrigin;
  useRelativeToContainerLayout?: boolean;
  dontStretch?: boolean;
  componentName?: string;
  styleId?: string | StyleRefOrStyleRefs;
  parentContainerRef?: CompRef;
  containerRef?: CompRef;
  componentDefinition?: CompStructure;
  cb?: (compRef: CompRef) => void;
} & Partial<AddWidgetBIParams>;

const addWidget = async function (
  appDefinitionId: string,
  widgetId: string,
  position: Partial<CompLayout>,
  options: AddWidgetOptions = {} as AddWidgetOptions,
) {
  options.containerRef =
    options.containerRef || editorAPI.sections.getViewportSection();
  tpaUtils.bi.reportBIOnAppIntent(
    editorAPI,
    bi.events.APP_INTENT,
    appDefinitionId,
    constants.BI.initiator.EDITOR,
    options.biOrigin || constants.BI.type.ADD_APP,
  );

  const widgetOptionsWithLayout = getAddWidgetOptions(
    widgetId,
    position,
    options,
    appDefinitionId,
  );

  return tpaAddRemoveDSService.addWidget(
    editorAPI,
    appDefinitionId,
    widgetOptionsWithLayout,
  );
};

const getAddWidgetOptions = function (
  widgetId: string,
  position: Partial<CompLayout>,
  options: AddWidgetOptions,
  appDefinitionId: string,
): tpaAddRemoveDSService.AddWidgetOptions {
  const widgetOptionsWithLayout = _.merge(
    { widgetId, layout: position },
    _.pick(options, WIDGET_OPTIONS),
  );

  if (util.sections.isSectionsEnabled()) {
    if (options.addingMethod === 'click') {
      const sectionLikeContainerToPaste =
        util.sections.getSectionLikeContainerToPaste(editorAPI, []);
      widgetOptionsWithLayout.containerRef = sectionLikeContainerToPaste;
      widgetOptionsWithLayout.useRelativeToContainerLayout =
        widgetOptionsWithLayout.useRelativeToContainerLayout ?? true;
    } else if (options.containerRef) {
      widgetOptionsWithLayout.useRelativeToContainerLayout =
        widgetOptionsWithLayout.useRelativeToContainerLayout ?? true;
    }
  }
  // @ts-expect-error
  widgetOptionsWithLayout.callback = function (obj: { comp: CompRef }) {
    editorAPI.bi.tryFlushingConditionedEvents();
    const appData = editorAPI.dsRead.tpa.app.getDataByAppDefId(appDefinitionId);
    // @ts-expect-error
    sendAddWidgetBiEvent(appData, obj.comp, widgetId, options?.presetId);
    if (options?.cb) {
      options.cb(obj.comp);
    }
  };
  // @ts-expect-error
  return widgetOptionsWithLayout;
};

const setStyleParam = function (appDefId: any, compId: any, options: any) {
  tpaStyleService.setStyleParam(editorAPI, compId, options, undefined);
};

const isInstalled = function (appDefinitionId: string) {
  const installed = editorAPI.dsRead.tpa.app.isInstalled(appDefinitionId);
  if (installed) {
    editorAPI.dsRead.tpa.appMarket.getData(appDefinitionId);
  }
  return installed;
};

const getFirstAppCompPageId = function (appDefinitionId: string) {
  return editorAPI.dsRead.tpa.app.getFirstAppCompPageId(appDefinitionId);
};

const sendAddWidgetBiEvent = function (
  appData: AppData,
  compRef: CompRef,
  widgetId: string,
  presetId?: string,
) {
  const params = {
    app_id: appData.appDefinitionId,
    app_site_id: appData.instanceId,
    instance_id: compRef.id,
    widget_id: widgetId,
    page_id: compRef.pageId,
    preset_id: presetId,
  };
  editorAPI.bi.event(bi.events.EXTENSION_ADDED, params);
};

const sendSectionBiEvent = function (
  appData: AppData,
  sectionObj: { sectionId: any; page: { id: any } },
  widgetId: string,
  origin?: string,
) {
  const params = {
    app_id: appData.appDefinitionId,
    app_site_id: appData.instanceId,
    instance_id: sectionObj.sectionId,
    widget_id: widgetId,
    page_id: sectionObj.page.id,
    origin,
  };
  editorAPI.bi.event(bi.events.EXTENSION_ADDED, params);
};

const getSectionRefByPageId = function (pageId: string) {
  return editorAPI.dsRead.tpa.section.getSectionRefByPageId(pageId);
};

const upgrade = function (
  appDefinitionId: string,
  external: boolean,
  origin: string,
) {
  fireAppIntentBI(constants.BI.type.APP_UPGRADE_BUTTON, appDefinitionId);
  packagePicker.startUpgradeFlow(editorAPI, appDefinitionId, false, {
    origin,
  });
};

const hasPremiumOffering = experiment.isOpen('se_appDefIdUpdates')
  ? () => editorAPI.dsRead.tpaV2.app.hasPremiumOffering
  : function (appDefinitionId: string) {
      const appData =
        editorAPI.dsRead.tpa.app.getDataByAppDefId(appDefinitionId);
      if (appData) {
        return editorAPI.dsRead.tpa.app.hasPremiumOffering(
          appData.applicationId,
        );
      }
      return false;
    };

const openSuperAppPanel = function (
  superAppId: string,
  isAppInstalled: boolean,
  panelName: string,
  buttonLabel: string,
  buttonPromotionalLabel: string,
  panelClassName: string,
  symbolName: string,
) {
  superAppMenuInfra.button.registerButton(
    editorAPI,
    superAppId,
    isAppInstalled,
    panelName,
    buttonLabel,
    buttonPromotionalLabel,
    panelClassName,
    symbolName,
    true,
  );
  editorAPI.panelManager.openPanel(panelName, {
    appWasManuallyInstalled: true,
  });
};

const openSettingsOpenModal = function (params: any, selectedComponents: any) {
  tpaSettingsModalService.settingsOpenModal(
    editorAPI,
    {
      data: _.omit(params, 'autoProvision'),
    },
    _.noop,
    selectedComponents,
  );
};

const openSuperAppsModal = function (
  params: {
    autoProvision?: any;
    url?: any;
    width?: number;
    height?: number;
    isBareMode?: boolean;
    appDefinitionId?: any;
    bi?: any;
  },
  _editorAPI: undefined,
  selectedComponents: undefined,
) {
  params = params || {};
  const appDefId =
    params.appDefinitionId || getApplicationDefinitionIdFromSelectedComp();
  fireAppIntentBI(constants.BI.type.APP_MANAGE_BUTTON, appDefId);
  params.bi = tpaUtils.bi.getAppIntentBiParamsToAddToUrl(
    editorAPI,
    appDefId,
    constants.BI.type.APP_MANAGE_BUTTON,
  );

  if (params?.autoProvision) {
    // @ts-expect-error
    const appData: AppData =
      editorAPI.dsRead.tpa.app.getDataByAppDefId(appDefId);
    const isDraft = editorAPI.dsRead.generalInfo.isDraft();
    if (appData) {
      if (appData.notProvisioned || appData.demoMode || isDraft) {
        editorAPI.saveManager.saveInBackground(
          _.partial(openSettingsOpenModal, params),
          _.partial(
            tpaAlertsService.openSaveFailedAlertForDashboardUrl,
            editorAPI,
            appData.appDefinitionId,
          ),
          'openSuperAppsModal',
          {
            sourceOfStart: 'openSuperAppsModal_bgSave',
          },
        );
      } else {
        openSettingsOpenModal(params, selectedComponents);
      }
    }
  } else {
    openSettingsOpenModal(params, selectedComponents);
  }
};

const fireAppIntentBI = function (type: string, appDefId: string) {
  tpaUtils.bi.reportBIOnAppIntent(
    editorAPI,
    bi.events.APP_INTENT,
    appDefId,
    constants.BI.initiator.EDITOR,
    type,
  );
};

const getApplicationDefinitionIdFromSelectedComp = function () {
  const compData = editorAPI.selection.getSelectedComponentData();
  const appData = editorAPI.dsRead.tpa.app.getData(compData?.applicationId);
  return appData?.appDefinitionId;
};

const waitForAppCompleteInstallation = (
  { store, dsRead }: EditorAPI,
  appDefId: string,
) => {
  const findInstalledApp = () =>
    dsRead.platform
      .getInstalledEditorApps()
      .find((app) => app.appDefinitionId === appDefId);
  const hasDescriptor = () =>
    Boolean(dsRead.platform.getAppDescriptor(appDefId));
  const hasAppBeenCompletelyInstalled = () =>
    hasDescriptor() && findInstalledApp();

  return new Promise<PlatformAppDescription>((resolve) => {
    if (hasAppBeenCompletelyInstalled()) {
      resolve(findInstalledApp());
    } else {
      const unsubscribe = store.subscribe(() => {
        if (hasAppBeenCompletelyInstalled()) {
          unsubscribe();

          resolve(findInstalledApp());
        }
      });
    }
  });
};

export {
  init,
  isPremium,
  getExtensions,
  openDashboardUrl,
  openTranslationDashboardUrl,
  refreshAppCompsForAppDefId,
  registerOnAppInstalled,
  registerOnAppDeleted,
  addApp,
  addWidget,
  addSection,
  superAppsConstants,
  isInstalled,
  getFirstAppCompPageId,
  getSectionRefByPageId,
  upgrade,
  hasPremiumOffering,
  openSuperAppPanel,
  openSuperAppsModal,
  setStyleParam,
  onRefWidgetDrop,
  onRefWidgetClick,
  addAppBuilderWidgetToSite,
  waitForAppCompleteInstallation,
};
